import { createFileRoute } from "@tanstack/react-router";
import {
  CalendarCheck,
  Check,
  ChevronDown,
  Phone,
  PiggyBank,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";

import { Reveal } from "../components/reveal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { HomeTrustBadges } from "../components/home-trust-badges";
import { HomeCarePlanGrid } from "../components/homecare-plan-grid";

import { SocialProofStrip } from "../components/special-landing-extras";
import { GalleryVideoSlider } from "../components/gallery-video-slider";
import {
  HOMECARE_ADDONS,
  HOMECARE_COMPARE,
  HOMECARE_FAQS,
  HOMECARE_PLANS,
  HOMECARE_TERMS,
} from "../data/homecare-plans";
import ogImage from "../assets/homecare-og.jpg";
import { InlineQuoteSection } from "@/components/inline-quote-section";
import { TopQuoteForm } from "@/components/top-quote-form";

const SITE = "https://capitalpropressurewashing.com";
const URL = `${SITE}/annual-maintenance`;
const TITLE = "Home Care Maintenance Plans | Capital Pro Pressure Washing";
const DESC =
  "Capital Pro Home Care memberships from $65/month: scheduled house washing, gutter maintenance services, roof & gutter visual checks, window cleaning, priority scheduling and 15% off every add-on across New York's Capital Region.";
const OG_IMAGE = `${SITE}${ogImage}`;

const BENEFITS = [
  {
    icon: CalendarCheck,
    title: "Priority Scheduling",
    body: "Members get first pick of dates in our busiest spring and fall weeks — and go to the front of the line for add-on work.",
  },
  {
    icon: PiggyBank,
    title: "Locked-In Pricing",
    body: "Your monthly rate is fixed for the year. No seasonal price jumps, no surprise invoices when the crew shows up.",
  },
  {
    icon: Sparkles,
    title: "15% Off Every Add-On",
    body: "Roof treatments, decks, pavers, fences and holiday lighting all come at member pricing, all year.",
  },
  {
    icon: ShieldCheck,
    title: "We Keep an Eye on It",
    body: "We don't just clean your home — we keep an eye on it. Every visit, your crew checks your roof and gutters and lets you know if anything looks off.",
  },
];

const STEPS = [
  {
    n: "1",
    title: "Choose Your Plan",
    body: "Pick Essential, Complete or Premium. Tell us about your home and we confirm the fit before anything is billed.",
  },
  {
    n: "2",
    title: "We Schedule Your Year",
    body: "We map out every visit for the season and text you before each one. Nothing for you to remember or chase down.",
  },
  {
    n: "3",
    title: "We Show Up & Prove It",
    body: "Our own crew does the work — never subcontractors — and you get a text when we're done, with photos if we spotted anything worth flagging.",
  },
];

export const Route = createFileRoute("/annual-maintenance")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Capital Pro Home Care Memberships" },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Capital Pro Home Care Memberships" },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Capital Pro Home Care Membership",
          serviceType: "Annual exterior cleaning maintenance plan",
          description: DESC,
          url: URL,
          image: OG_IMAGE,
          provider: { "@id": `${SITE}/#business` },
          areaServed: {
            "@type": "AdministrativeArea",
            name: "Capital Region, New York",
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Home Care Membership Plans",
            itemListElement: HOMECARE_PLANS.map((p) => ({
              "@type": "Offer",
              name: `Home Care ${p.name}`,
              price: p.price,
              priceCurrency: "USD",
              url: URL,
              availability: "https://schema.org/InStock",
              itemOffered: {
                "@type": "Service",
                name: `Home Care ${p.name}`,
              },
            })),
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: HOMECARE_FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: HomeCarePage,
});

function Cell({ value }: { value: boolean | string }) {
  if (value === true)
    return <Check className="mx-auto text-[var(--brand-deep-blue)]" size={20} strokeWidth={2.5} />;
  if (value === false) return <X className="mx-auto text-gray-400" size={20} strokeWidth={2.5} />;
  return <span className="text-sm text-[var(--brand-charcoal)]/85">{value}</span>;
}

function HomeCarePage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Home Care Memberships" }]} />

      {/* Hero */}
      <section className="bg-[var(--brand-deep-blue)] text-white py-14 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4 text-center">
          <p
            className="text-[#ff6b7f] font-semibold uppercase tracking-[0.2em] text-sm mb-3"
            style={{ fontFamily: "var(--font-display)" }}
          >
            Capital Pro Home Care
          </p>
          <h1
            className="text-3xl md:text-5xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Year-Round Curb Appeal, One Monthly Payment
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            Memberships from $65 a month. We schedule your house wash, gutter maintenance services, roof &
            gutter visual checks and window cleaning for the whole year, put you first in line for
            dates, and take 15% off everything else we do.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="#plans"
              className="btn-pop inline-flex items-center justify-center rounded-md bg-[var(--brand-red)] text-white px-7 py-4 uppercase tracking-wide hover:bg-[var(--brand-red-hover)] transition-colors"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              See Plans
            </a>
            <a
              href="tel:+15189001913"
              data-analytics="call_click"
              data-source="homecare-hero"
              className="btn-pop inline-flex items-center justify-center gap-2 rounded-md bg-white text-[var(--brand-deep-blue)] px-7 py-4 uppercase tracking-wide hover:opacity-90 transition-opacity"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
          <p className="mt-5 text-white/70 text-sm">
            Cancel any time with 30 days' notice · Serving New York's Capital Region for 10 years
          </p>
          <p className="mt-2 text-white/55 text-xs">
            <a href="#plans" className="underline hover:text-white/80 transition-colors">
              See the plans & pricing
            </a>
          </p>
        </div>
      </section>
      <TopQuoteForm source="Home Care Top" defaultService="homecare" heading="Get Your Home Care Membership Quote" />

      <SocialProofStrip />

      <HomeTrustBadges />

      {/* Why a membership */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight text-center mb-10"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Why Homeowners Join
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b, i) => {
              const Icon = b.icon;
              return (
                <Reveal
                  key={b.title}
                  delayMs={i * 70}
                  className="card-pop h-full rounded-lg border border-gray-200 bg-white p-6"
                >
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-md bg-[var(--brand-red)]/10 text-[var(--brand-red)]">
                    <Icon size={22} />
                  </div>
                  <h3
                    className="text-[var(--brand-deep-blue)] text-lg uppercase"
                    style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                  >
                    {b.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--brand-charcoal)]/85">
                    {b.body}
                  </p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section id="plans" className="scroll-mt-24 bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1200px] px-4">
          <div className="mb-10 text-center">
            <p
              className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-[var(--brand-red)]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              Membership Plans
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Pick The Plan That Fits Your Home
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-[var(--brand-charcoal)]/75">
              Pricing shown covers homes up to 2,500 sqft. Larger properties are quoted
              individually at member rates.
            </p>
          </div>
          {/* Risk reversal */}
          <div className="mx-auto mb-8 flex max-w-2xl flex-wrap items-center justify-center gap-x-4 gap-y-2 rounded-lg bg-[var(--brand-light-gray)] px-5 py-3 text-center text-sm text-[var(--brand-charcoal)]/80">
            <span className="flex items-center gap-1.5">
              <Check className="h-4 w-4 text-[var(--brand-red)]" strokeWidth={3} aria-hidden="true" />
              No contract
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="h-4 w-4 text-[var(--brand-red)]" strokeWidth={3} aria-hidden="true" />
              Cancel anytime with 30 days' notice
            </span>
            <span className="flex items-center gap-1.5">
              <Check className="h-4 w-4 text-[var(--brand-red)]" strokeWidth={3} aria-hidden="true" />
              We confirm everything before your first visit
            </span>
          </div>
          <HomeCarePlanGrid />
          <p className="mx-auto mt-6 max-w-2xl text-center text-xs leading-relaxed text-[var(--brand-charcoal)]/60">
            Annual gutter maintenance services include a standard gutter cleanout or debris removal
            from accessible gutter guards. Removal and reinstallation of gutter guards or specialty
            gutter systems may require an additional charge based on your home's gutter system.
          </p>
        </div>
      </section>

      {/* Value stack — one-off vs. membership */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-4xl px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight text-center mb-3"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Why A Membership Pays For Itself
          </h2>
          <p className="mx-auto mt-2 mb-9 max-w-2xl text-center text-[var(--brand-charcoal)]/75">
            Here's what the same work costs if you book it one-off — compared to what a Complete
            member pays per month.
          </p>
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <div className="flex items-center justify-between gap-4 bg-[var(--brand-light-gray)] px-5 py-4">
              <span className="text-sm font-medium text-[var(--brand-charcoal)] sm:text-base">1 professional house wash</span>
              <span className="text-base font-bold text-[var(--brand-deep-blue)] sm:text-lg" style={{ fontFamily: "var(--font-display)" }}>~$350</span>
            </div>
            <div className="flex items-center justify-between gap-4 bg-white px-5 py-4">
              <span className="text-sm font-medium text-[var(--brand-charcoal)] sm:text-base">2 gutter maintenance services</span>
              <span className="text-base font-bold text-[var(--brand-deep-blue)] sm:text-lg" style={{ fontFamily: "var(--font-display)" }}>~$400</span>
            </div>
            <div className="flex items-center justify-between gap-4 bg-[var(--brand-light-gray)] px-5 py-4">
              <span className="text-sm font-medium text-[var(--brand-charcoal)] sm:text-base">1 exterior window cleaning (all windows)</span>
              <span className="text-base font-bold text-[var(--brand-deep-blue)] sm:text-lg" style={{ fontFamily: "var(--font-display)" }}>~$249</span>
            </div>
            <div className="flex items-center justify-between gap-4 border-t border-gray-200 bg-white px-5 py-4">
              <span className="text-sm font-semibold uppercase tracking-wide text-[var(--brand-charcoal)]/70">One-off total</span>
              <span className="text-lg font-bold text-[var(--brand-charcoal)]/50 line-through sm:text-xl" style={{ fontFamily: "var(--font-display)" }}>~$999/yr</span>
            </div>
            <div className="flex items-center justify-between gap-4 bg-[var(--brand-deep-blue)] px-5 py-5 text-white">
              <span className="text-sm font-semibold uppercase tracking-wide">Complete member</span>
              <span className="flex items-baseline gap-1">
                <span className="text-3xl font-bold sm:text-4xl" style={{ fontFamily: "var(--font-display)" }}>$80</span>
                <span className="text-sm font-semibold opacity-80">/mo</span>
              </span>
            </div>
          </div>
          <div className="mx-auto mt-5 max-w-3xl rounded-lg border border-[var(--brand-bright-blue)]/30 bg-white px-5 py-4">
            <p className="text-center text-sm font-semibold uppercase tracking-wide text-[var(--brand-bright-blue)]" style={{ fontFamily: "var(--font-display)" }}>
              Member-only extras included in that price
            </p>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-[var(--brand-charcoal)]/85">
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-[var(--brand-red)]" strokeWidth={3} aria-hidden="true" />
                Priority scheduling
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-[var(--brand-red)]" strokeWidth={3} aria-hidden="true" />
                Seasonal reminders
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-[var(--brand-red)]" strokeWidth={3} aria-hidden="true" />
                Roof & gutter visual check (bonus)
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="h-4 w-4 text-[var(--brand-red)]" strokeWidth={3} aria-hidden="true" />
                15% off every add-on
              </span>
            </div>
          </div>
          <p className="mt-4 text-center text-sm text-[var(--brand-charcoal)]/70">
            For $80 a month — less than booking those three jobs one-off — you get priority
            scheduling, automatic reminders, a free roof & gutter visual check on every wash, and
            15% off everything else — all year.
          </p>
          <p className="mt-2 text-center text-xs text-[var(--brand-charcoal)]/55">
            Example pricing only — every home is quoted individually before any work begins.
          </p>
        </div>
      </section>

      {/* Comparison */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight text-center mb-10"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            What's Included
          </h2>

          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-lg border border-gray-200 bg-white md:block">
            <table className="w-full border-collapse text-center">
              <caption className="sr-only">
                Comparison of Capital Pro Home Care membership plans
              </caption>
              <thead>
                <tr className="bg-[var(--brand-deep-blue)] text-white">
                  <th
                    scope="col"
                    className="p-4 text-left text-sm uppercase tracking-wide"
                    style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                  >
                    Feature
                  </th>
                  {HOMECARE_PLANS.map((p) => (
                    <th
                      key={p.id}
                      scope="col"
                      className="p-4 text-sm uppercase tracking-wide"
                      style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                    >
                      {p.name}
                      <span className="block text-xs font-semibold normal-case opacity-80">
                        ${p.price}/mo
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {HOMECARE_COMPARE.map((row, i) => (
                  <tr key={row.label} className={i % 2 ? "bg-[var(--brand-light-gray)]/60" : ""}>
                    <th
                      scope="row"
                      className="p-4 text-left text-sm font-semibold text-[var(--brand-charcoal)]"
                    >
                      {row.label}
                    </th>
                    <td className="p-4">
                      <Cell value={row.essential} />
                    </td>
                    <td className="p-4">
                      <Cell value={row.complete} />
                    </td>
                    <td className="p-4">
                      <Cell value={row.premium} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile stacked */}
          <div className="space-y-5 md:hidden">
            {HOMECARE_PLANS.map((p) => (
              <div key={p.id} className="rounded-lg border border-gray-200 bg-white p-5">
                <h3
                  className="text-[var(--brand-deep-blue)] text-xl uppercase"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {p.name} — ${p.price}/mo
                </h3>
                <ul className="mt-3 space-y-2 text-sm text-[var(--brand-charcoal)]/85">
                  {HOMECARE_COMPARE.filter((r) => r[p.id] !== false).map((r) => (
                    <li key={r.label} className="flex items-start gap-2">
                      <Check
                        className="mt-0.5 h-4 w-4 shrink-0 text-[var(--brand-red)]"
                        strokeWidth={3}
                        aria-hidden="true"
                      />
                      <span>
                        {r.label}
                        {typeof r[p.id] === "string" ? ` — ${r[p.id]}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1000px] px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight text-center"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Members Save 15% On Everything Else
          </h2>
          <p className="mx-auto mt-3 mb-9 max-w-2xl text-center text-[var(--brand-charcoal)]/75">
            Typical Capital Region pricing for common add-ons, and what a Home Care member pays
            for the same job.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {HOMECARE_ADDONS.map((a, i) => {
              const member = Math.round(a.typical * 0.85);
              return (
                <Reveal
                  key={a.name}
                  delayMs={i * 70}
                  className="flex h-full items-center justify-between gap-4 rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-5"
                >
                  <div>
                    <h3
                      className="text-[var(--brand-deep-blue)] text-base uppercase"
                      style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                    >
                      {a.name}
                    </h3>
                    <p className="mt-1 text-xs text-[var(--brand-charcoal)]/65">{a.note}</p>
                  </div>
                  <div className="shrink-0 text-right">
                    <span className="block text-sm text-[var(--brand-charcoal)]/55 line-through">
                      ${a.typical}
                    </span>
                    <span
                      className="block text-2xl leading-none text-[var(--brand-red)]"
                      style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                    >
                      ${member}
                    </span>
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-[var(--brand-charcoal)]/60">
                      Member price
                    </span>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-5 text-center text-xs text-[var(--brand-charcoal)]/60">
            Example pricing only — every add-on is quoted for your property before any work
            begins.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[var(--brand-light-gray)] py-14 md:py-16">
        <div className="mx-auto max-w-[1100px] px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight text-center mb-10"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            How Membership Works
          </h2>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <Reveal
                key={s.n}
                delayMs={i * 90}
                className="card-pop h-full rounded-lg border border-gray-200 bg-white p-6 text-center"
              >
                <span
                  className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--brand-deep-blue)] text-xl text-white"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {s.n}
                </span>
                <h3
                  className="text-[var(--brand-deep-blue)] text-lg uppercase"
                  style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
                >
                  {s.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[var(--brand-charcoal)]/85">
                  {s.body}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* See the crew in action */}
      <section className="bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[1100px] px-4">
          <div className="mb-9 text-center">
            <p
              className="mb-3 text-sm font-semibold uppercase tracking-[0.2em] text-[var(--brand-red)]"
              style={{ fontFamily: "var(--font-display)" }}
            >
              See The Crew In Action
            </p>
            <h2
              className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              Real Work From Across The Capital Region
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-[var(--brand-charcoal)]/75">
              Swipe through the clips to see our own crew in action — no subcontractors, no stock
              footage. This is the same level of care every member gets on every visit.
            </p>
          </div>
          <GalleryVideoSlider />
          <p className="mt-6 text-center text-sm text-[var(--brand-charcoal)]/70">
            Every member visit is handled with the same care you see here — and you get a text with
            photos when we're done.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="scroll-mt-24 bg-white py-14 md:py-16">
        <div className="mx-auto max-w-[860px] px-4">
          <h2
            className="text-[var(--brand-deep-blue)] text-3xl md:text-4xl uppercase leading-tight text-center mb-10"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Membership Questions
          </h2>
          <div className="space-y-3">
            {HOMECARE_FAQS.map((f, i) => (
              <Reveal key={f.q} delayMs={i * 40}>
                <details className="group rounded-lg border border-gray-200 bg-[var(--brand-light-gray)] p-5">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-base font-semibold text-[var(--brand-deep-blue)]">
                    {f.q}
                    <ChevronDown
                      className="h-5 w-5 shrink-0 transition-transform group-open:rotate-180"
                      aria-hidden="true"
                    />
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-[var(--brand-charcoal)]/85">
                    {f.a}
                  </p>
                </details>
              </Reveal>
            ))}
          </div>

          {/* Terms */}
          <details className="mt-8 rounded-lg border border-gray-200 p-5">
            <summary className="cursor-pointer text-sm font-semibold uppercase tracking-wide text-[var(--brand-charcoal)]/70">
              Membership terms
            </summary>
            <ul className="mt-3 space-y-2 text-xs leading-relaxed text-[var(--brand-charcoal)]/70">
              {HOMECARE_TERMS.map((t) => (
                <li key={t}>• {t}</li>
              ))}
            </ul>
          </details>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[var(--brand-deep-blue)] py-14 text-white md:py-16">
        <div className="mx-auto max-w-[900px] px-4 text-center">
          <h2
            className="text-3xl md:text-4xl uppercase leading-tight"
            style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
          >
            Ready To Stop Thinking About It?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-white/85">
            Tell us about your home and we'll confirm the right Home Care plan, your visit
            schedule and your monthly rate — usually the same day.
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href="#plans"
              className="btn-pop inline-flex items-center justify-center rounded-md bg-[var(--brand-red)] px-7 py-4 uppercase tracking-wide text-white transition-colors hover:bg-[var(--brand-red-hover)]"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              See Plans
            </a>
            <a
              href="tel:+15189001913"
              data-analytics="call_click"
              data-source="homecare-final-cta"
              className="btn-pop inline-flex items-center justify-center gap-2 rounded-md bg-white px-7 py-4 uppercase tracking-wide text-[var(--brand-deep-blue)] transition-opacity hover:opacity-90"
              style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}
            >
              <Phone size={18} strokeWidth={2.5} />
              Call (518) 900-1913
            </a>
          </div>
        </div>
      </section>
      <InlineQuoteSection source="Home Care Membership" defaultService="homecare" />
    </main>
  );
}
